/**
 * RolesAndPermissionsAddPopup
 *
 * Prototype dialog to create a new role with a permissions matrix. Uses local state
 * for the role name and selected permissions; currently does not persist to the API.
 * Save is disabled until a role name is provided and at least one permission is picked.
 */
import { useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  IconButton,
  TextField,
  Typography,
  Box,
  InputLabel,
  TableContainer,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./roles-and-permissions-add-popup.css";

// Helper to trim leading/trailing dashes without regex
function trimDashes(str) {
  let start = 0;
  let end = str.length;
  while (start < end && str[start] === "-") start++;
  while (end > start && str[end - 1] === "-") end--;
  return str.slice(start, end);
}

const RolesAndPermissionsAddPopup = ({ isOpen, onClose }) => {
  const [roleName, setRoleName] = useState("");
  const [isSelectAll, setIsSelectAll] = useState(false);

  const modules = [
    "Objectives",
    "Key Results",
    "Initiatives",
    "Milestones",
    "Statuses",
    "Permissions",
  ];
  const permissions = ["A", "R", "W", "D"];

  const [selectedPermissions, setSelectedPermissions] = useState(
    modules.reduce(
      (acc, module) => ({
        ...acc,
        [module]: permissions.reduce(
          (pAcc, perm) => ({ ...pAcc, [perm]: false }),
          {},
        ),
      }),
      {},
    ),
  );

  const handlePermissionChange = (module, action) => {
    setSelectedPermissions((prev) => ({
      ...prev,
      [module]: {
        ...prev[module],
        [action]: !prev[module][action],
      },
    }));
  };

  const handleSelectAll = () => {
    const newValue = !isSelectAll;
    setIsSelectAll(newValue);
    setSelectedPermissions(
      modules.reduce(
        (acc, module) => ({
          ...acc,
          [module]: permissions.reduce(
            (pAcc, perm) => ({ ...pAcc, [perm]: newValue }),
            {},
          ),
        }),
        {},
      ),
    );
  };
  const isSaveDisabled = () => {
    if (!roleName.trim()) return true; // role name empty
    return !Object.values(selectedPermissions).some((permObj) =>
      Object.values(permObj).includes(true),
    );
  };

  const handleSave = () => {
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      maxWidth="md"
      fullWidth
      className="add-role-popup"
      onClose={(event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
    >
      {/* Header */}
      <DialogTitle className="popup-header">
        Add New Role
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ fontSize: 19 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers className="popup-content">
        <InputLabel className="role-label">Role Name</InputLabel>
        <TextField
          fullWidth
          placeholder="Enter a role name"
          variant="outlined"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          margin="normal"
          className="role-input"
        />

        <Box className="legend-selectall-row">
          <Typography variant="body2" className="legend-text">
            <strong>A</strong> - Approvals | <strong>R</strong> - Read |{" "}
            <strong>W</strong> - Write | <strong>D</strong> - Delete
          </Typography>

          <Box className="select-all-box">
            <Checkbox
              id="roles-select-all"
              checked={isSelectAll}
              onChange={handleSelectAll}
              slotProps={{
                input: {
                  id: "roles-select-all-input",
                  "aria-labelledby": "roles-select-all-label",
                  "aria-label": isSelectAll
                    ? "Select all permissions (all selected)"
                    : "Select all permissions",
                },
              }}
            />
            <span
              id="roles-select-all-label"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                overflow: "hidden",
                clip: "rect(0 0 0 0)",
                whiteSpace: "nowrap",
              }}
            >
              Select All Permissions
            </span>
            <span aria-hidden>{"Select All"}</span>
          </Box>
        </Box>

        <TableContainer component={Paper} className="table-container">
          <Table className="permissions-table">
            <TableHead>
              <TableRow>
                <TableCell className="table-head">
                  Modules Permissions
                </TableCell>
                {permissions.map((perm) => (
                  <TableCell
                    key={perm}
                    align="center"
                    className="table-head-permissions"
                  >
                    {perm}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {modules.map((module) => (
                <TableRow key={module}>
                  <TableCell className="module-name">{module}</TableCell>
                  {permissions.map((action, idx) => {
                    const slug = (s) => {
                      let out = String(s)
                        .toLowerCase()
                        .replaceAll(/[^a-z0-9]+/gi, "-");
                      out = trimDashes(out);
                      return out;
                    };
                    const checkboxId = `perm-${slug(module)}-${slug(action)}`;
                    const labelId = `label-${checkboxId}`;
                    const labelText = `${module} - ${action} permission`;
                    return (
                      <TableCell key={action} align="center">
                        <Checkbox
                          id={checkboxId}
                          checked={selectedPermissions[module][action]}
                          onChange={() =>
                            handlePermissionChange(module, action)
                          }
                          slotProps={{
                            input: {
                              id: `${checkboxId}-input`,
                              "aria-labelledby": labelId,
                              "aria-label":
                                labelText +
                                (selectedPermissions[module][action]
                                  ? " (selected)"
                                  : ""),
                            },
                          }}
                        />
                        <span
                          id={labelId}
                          style={{
                            position: "absolute",
                            width: 1,
                            height: 1,
                            overflow: "hidden",
                            clip: "rect(0 0 0 0)",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {labelText}
                        </span>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions className="popup-footer">
        <Button onClick={onClose} variant="outlined" className="secondary">
          Cancel
        </Button>
        <Box>
          <Button variant="outlined" sx={{ mr: 1 }} className="secondary">
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            className="primary"
            disabled={isSaveDisabled()}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

RolesAndPermissionsAddPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RolesAndPermissionsAddPopup;
